import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import {
  copyMenuToOutlet,
  copyMenuToAllOutlets,
  clearOutletMenu,
  getOutletMenuStats
} from '../../utils/outletMenuMigration';
import './OutletMenus.css';

const OutletMenus = () => {
  const [outlets, setOutlets] = useState([]);
  const [menuStats, setMenuStats] = useState({});
  const [globalMenuCount, setGlobalMenuCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);

  // Load outlets and stats
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load outlets
      const outletsSnapshot = await getDocs(collection(db, 'outlets'));
      const outletsData = outletsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setOutlets(outletsData.filter(o => o.isActive));

      // Load global menu count
      const menuSnapshot = await getDocs(collection(db, 'menu_items'));
      setGlobalMenuCount(menuSnapshot.size);

      // Load stats for each outlet
      const stats = {};
      for (const outlet of outletsData) {
        stats[outlet.id] = await getOutletMenuStats(outlet.id);
      }
      setMenuStats(stats);

      setLoading(false);
    } catch (error) {
      console.error('Error loading data:', error);
      setLoading(false);
    }
  };

  const handleCopyToOutlet = async (outletId, outletName) => {
    if (!window.confirm(`Copy ${globalMenuCount} menu items to "${outletName}"?`)) {
      return;
    }

    setProcessing(true);
    setResult(null);

    const res = await copyMenuToOutlet(outletId);

    if (res.success) {
      setResult({
        type: 'success',
        message: `✅ Successfully copied ${res.count} items to ${outletName}`
      });
      await loadData(); // Refresh stats
    } else {
      setResult({
        type: 'error',
        message: `❌ Error: ${res.error}`
      });
    }

    setProcessing(false);
  };

  const handleCopyToAll = async () => {
    if (!window.confirm(`Copy ${globalMenuCount} menu items to ALL ${outlets.length} outlets?\n\nThis will overwrite existing outlet menus.`)) {
      return;
    }

    setProcessing(true);
    setResult(null);

    const res = await copyMenuToAllOutlets();

    if (res.success) {
      setResult({
        type: 'success',
        message: `✅ Successfully copied menu to ${res.outletsProcessed} outlets (${res.totalCopied} total items)`
      });
      await loadData(); // Refresh stats
    } else {
      setResult({
        type: 'error',
        message: `❌ Error: ${res.error}`
      });
    }

    setProcessing(false);
  };

  const handleClearMenu = async (outletId, outletName) => {
    if (!window.confirm(`Delete ALL menu items from "${outletName}"?\n\nThis action cannot be undone!`)) {
      return;
    }

    setProcessing(true);
    setResult(null);

    const res = await clearOutletMenu(outletId);

    if (res.success) {
      setResult({
        type: 'success',
        message: `✅ Cleared ${res.count} items from ${outletName}`
      });
      await loadData(); // Refresh stats
    } else {
      setResult({
        type: 'error',
        message: `❌ Error: ${res.error}`
      });
    }

    setProcessing(false);
  };

  if (loading) {
    return (
      <div className="outlet-menus-page">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="outlet-menus-page">
      <div className="page-header">
        <h1>Outlet Menu Management</h1>
        <p>Manage separate menus for each outlet</p>
      </div>

      {result && (
        <div className={`result-message ${result.type}`}>
          {result.message}
          <button onClick={() => setResult(null)}>×</button>
        </div>
      )}

      <div className="global-menu-card">
        <div className="card-header">
          <h2>📋 Global Menu</h2>
          <span className="count-badge">{globalMenuCount} items</span>
        </div>
        <p>This is the master menu in the <code>menu_items</code> collection.</p>
        <button
          className="btn-primary"
          onClick={handleCopyToAll}
          disabled={processing || globalMenuCount === 0}
        >
          {processing ? '⏳ Processing...' : `📤 Copy to All ${outlets.length} Outlets`}
        </button>
      </div>

      <div className="outlets-grid">
        {outlets.map((outlet) => {
          const stats = menuStats[outlet.id] || {};

          return (
            <div key={outlet.id} className="outlet-menu-card">
              <div className="outlet-header">
                <div className="outlet-info">
                  <h3>📍 {outlet.name}</h3>
                  <span className="outlet-location">{outlet.address}</span>
                </div>
                {stats.hasMenu && (
                  <span className="status-badge active">Menu Active</span>
                )}
              </div>

              <div className="menu-stats">
                {stats.hasMenu ? (
                  <>
                    <div className="stat-row">
                      <span>Total Items:</span>
                      <strong>{stats.total}</strong>
                    </div>
                    <div className="stat-row">
                      <span>Available:</span>
                      <strong className="text-success">{stats.available}</strong>
                    </div>
                    <div className="stat-row">
                      <span>Unavailable:</span>
                      <strong className="text-muted">{stats.unavailable}</strong>
                    </div>
                    <div className="stat-row">
                      <span>Veg / Non-Veg:</span>
                      <strong>{stats.veg} / {stats.nonVeg}</strong>
                    </div>
                    <div className="stat-row">
                      <span>Categories:</span>
                      <strong>{stats.categories?.length || 0}</strong>
                    </div>
                  </>
                ) : (
                  <div className="no-menu-message">
                    <span className="icon">📭</span>
                    <p>No menu items for this outlet</p>
                  </div>
                )}
              </div>

              <div className="outlet-actions">
                <button
                  className="btn-copy"
                  onClick={() => handleCopyToOutlet(outlet.id, outlet.name)}
                  disabled={processing || globalMenuCount === 0}
                >
                  {stats.hasMenu ? '🔄 Update Menu' : '📥 Copy Menu'}
                </button>
                {stats.hasMenu && (
                  <button
                    className="btn-danger"
                    onClick={() => handleClearMenu(outlet.id, outlet.name)}
                    disabled={processing}
                  >
                    🗑️ Clear Menu
                  </button>
                )}
              </div>

              <div className="outlet-id-info">
                <small>Outlet ID: <code>{outlet.id}</code></small>
              </div>
            </div>
          );
        })}
      </div>

      <div className="info-box">
        <h3>ℹ️ How Outlet Menus Work</h3>
        <ul>
          <li><strong>Global Menu:</strong> Master menu in <code>menu_items</code> collection (used by admin panel)</li>
          <li><strong>Outlet Menus:</strong> Separate menus in <code>outlets/{'{'}outletId{'}'}/menu_items</code> (used by waiter app)</li>
          <li><strong>Copy Menu:</strong> Copies all items from global menu to specific outlet</li>
          <li><strong>Update Menu:</strong> Replaces outlet menu with current global menu</li>
          <li><strong>Clear Menu:</strong> Deletes all menu items from outlet (cannot be undone)</li>
        </ul>
        <div className="warning-box">
          <strong>⚠️ Important:</strong> After copying menus, edit individual outlet menus in the Foods page (select outlet first).
        </div>
      </div>
    </div>
  );
};

export default OutletMenus;
